import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { LangContext } from '../i18n/LangContext';
import { AuthContext } from '../contexts/AuthContext';
import { getProfilePhotoUrl, getLogoUrl, getServiceImageUrl } from '../config';

const ServiceDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  useContext(LangContext);
  const { user } = useContext(AuthContext);
  
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Modal de notification
  const [modal, setModal] = useState({ show: false, type: '', message: '' });
  
  // Formulaire de demande
  const [formData, setFormData] = useState({
    titre: '',
    description: '',
    details_supplementaires: '',
    priorite: 'normale',
    date_fin_prevue: ''
  });

  const loadService = async () => {
    try {
      const response = await api.get(`/services/${id}`);
      setService(response.data);
      setFormData(prev => ({
        ...prev,
        titre: `Demande pour: ${response.data.titre || response.data.nom}`
      }));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadService();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      navigate('/login', { state: { from: `/services/${id}` } });
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post('/service-requests', {
        service_id: parseInt(id),
        ...formData
      });
      
      setModal({
        show: true,
        type: 'success',
        message: 'Votre demande a été envoyée avec succès ! Vous pouvez suivre son évolution dans "Mes demandes".'
      });
    } catch (err) {
      setModal({
        show: true,
        type: 'error',
        message: err.response?.data?.error || 'Erreur lors de l\'envoi de la demande'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-blue-100">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-blue-100">
        <div className="text-center">
          <span className="text-6xl">❌</span>
          <p className="text-gray-600 mt-4">Service non trouvé</p>
          <Link to="/services" className="text-indigo-600 hover:underline mt-2 inline-block">
            Retour aux services
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 py-10 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Breadcrumb */}
        <div className="mb-6">
          <Link to="/services" className="text-indigo-600 hover:underline">
            ← Retour aux services
          </Link>
        </div>

        {/* Service Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header avec logo/gradient */}
          <div className="h-48 bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center relative">
            {service.logo ? (
              <img 
                src={getLogoUrl(service.logo)}
                alt={service.titre || service.nom}
                className="h-32 w-auto object-contain bg-white rounded-xl p-4 shadow-lg"
              />
            ) : (
              <span className="text-white text-6xl">💼</span>
            )}
            {service.categorie && (
              <span className="absolute top-4 right-4 px-3 py-1 bg-white/20 text-white rounded-full text-sm backdrop-blur">
                {service.categorie}
              </span>
            )}
          </div>

          {/* Content */}
          <div className="p-8">
            {/* Provider info */}
            {service.user_nom && (
              <div className="flex items-center gap-3 mb-4 pb-4 border-b">
                {service.photo_profil ? (
                  <img
                    src={getProfilePhotoUrl(service.photo_profil)}
                    alt={service.user_nom}
                    className="w-12 h-12 rounded-full object-cover border border-indigo-200"
                  />
                ) : (
                  <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                    <span className="text-indigo-600 font-bold text-lg">{service.user_nom[0]}</span>
                  </div>
                )}
                <div>
                  <p className="font-semibold text-gray-800">{service.user_nom}</p>
                  <p className="text-sm text-gray-500">Prestataire partenaire</p>
                </div>
              </div>
            )}

            <h1 className="text-3xl font-bold text-gray-800 mb-4">{service.titre || service.nom}</h1>
            
            <p className="text-gray-600 mb-6 whitespace-pre-line">{service.description}</p>

            {/* Galerie de photos du service */}
            {service.photos && (() => {
              try {
                const photos = JSON.parse(service.photos);
                if (photos && photos.length > 0) {
                  return (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-3">📷 Photos du service</h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {photos.map((photo, index) => (
                          <img
                            key={index}
                            src={getServiceImageUrl(photo)}
                            alt={`${service.titre} - ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg border border-gray-200 hover:scale-105 transition cursor-pointer"
                            onClick={() => window.open(getServiceImageUrl(photo), '_blank')}
                          />
                        ))}
                      </div>
                    </div>
                  );
                }
              } catch (e) {}
              return null;
            })()}

            {/* Contact info */}
            {(service.telephone || service.whatsapp || service.localisation) && (
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <h3 className="font-semibold text-gray-800 mb-3">📞 Contact</h3>
                <div className="space-y-2">
                  {service.telephone && (
                    <p className="text-gray-600">📱 Téléphone : <a href={`tel:${service.telephone}`} className="text-indigo-600 hover:underline">{service.telephone}</a></p>
                  )}
                  {service.whatsapp && (
                    <p className="text-gray-600">💬 WhatsApp : <a href={`https://wa.me/${service.whatsapp.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline">{service.whatsapp}</a></p>
                  )}
                  {service.localisation && (
                    <p className="text-gray-600">📍 Localisation : {service.localisation}</p>
                  )}
                </div>
              </div>
            )}

            {/* Info cards */}
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <div className="bg-indigo-50 rounded-xl p-4 text-center">
                <span className="text-3xl">💰</span>
                <p className="text-sm text-gray-500 mt-2">Tarif à partir de</p>
                <p className="text-2xl font-bold text-indigo-600">{service.tarif || service.prix || 0} XOF</p>
              </div>
              {service.delai && (
                <div className="bg-green-50 rounded-xl p-4 text-center">
                  <span className="text-3xl">⏱️</span>
                  <p className="text-sm text-gray-500 mt-2">Délai estimé</p>
                  <p className="text-xl font-bold text-green-600">{service.delai}</p>
                </div>
              )}
              <div className="bg-purple-50 rounded-xl p-4 text-center">
                <span className="text-3xl">✅</span>
                <p className="text-sm text-gray-500 mt-2">Service vérifié</p>
                <p className="text-lg font-bold text-purple-600">Qualité garantie</p>
              </div>
            </div>

            {/* CTA Button - Afficher seulement si le créateur du service a le module service_manager actif */}
            {service.can_receive_requests === false ? (
              <div className="bg-gray-100 rounded-xl p-6 text-center">
                <span className="text-4xl mb-3 block">⏸️</span>
                <p className="text-gray-600 font-medium">Ce service n'accepte pas de demandes pour le moment</p>
                <p className="text-sm text-gray-500 mt-2">Le prestataire n'a pas activé la réception de demandes.</p>
              </div>
            ) : !showRequestForm ? (
              <button
                onClick={() => setShowRequestForm(true)}
                className="w-full py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-lg font-semibold rounded-xl hover:from-indigo-700 hover:to-purple-700 transition shadow-lg"
              >
                🚀 Demander ce service
              </button>
            ) : (
              /* Request Form */
              <div className="bg-gray-50 rounded-xl p-6 mt-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-gray-800">📝 Formulaire de demande</h2>
                  <button 
                    onClick={() => setShowRequestForm(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ✕
                  </button>
                </div>

                {!user && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                    <p className="text-yellow-800">
                      ⚠️ Vous devez être connecté pour faire une demande.{' '}
                      <Link to="/login" className="text-indigo-600 font-medium hover:underline">
                        Se connecter
                      </Link>
                    </p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Titre de votre demande *
                    </label>
                    <input
                      type="text"
                      value={formData.titre}
                      onChange={(e) => setFormData({...formData, titre: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                      placeholder="Ex: Création de mon site web vitrine"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Décrivez votre besoin en détail *
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400 min-h-[150px]"
                      placeholder="Expliquez en détail ce que vous souhaitez obtenir, vos objectifs, vos contraintes..."
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Informations complémentaires
                    </label>
                    <textarea
                      value={formData.details_supplementaires}
                      onChange={(e) => setFormData({...formData, details_supplementaires: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                      placeholder="Références, exemples, liens utiles, contraintes particulières..."
                      rows={3}
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Priorité
                      </label>
                      <select
                        value={formData.priorite}
                        onChange={(e) => setFormData({...formData, priorite: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
                      >
                        <option value="basse">🟢 Basse - Pas pressé</option>
                        <option value="normale">🟡 Normale</option>
                        <option value="haute">🟠 Haute - Important</option>
                        <option value="urgente">🔴 Urgente</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date souhaitée (optionnel)
                      </label>
                      <input
                        type="date"
                        value={formData.date_fin_prevue}
                        onChange={(e) => setFormData({...formData, date_fin_prevue: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                  </div>

                  <div className="bg-blue-50 rounded-lg p-4 text-sm text-blue-800">
                    <p className="font-medium mb-1">💡 Comment ça marche ?</p>
                    <ol className="list-decimal list-inside space-y-1 text-blue-700">
                      <li>Vous soumettez votre demande</li>
                      <li>L'équipe du prestataire examine votre besoin</li>
                      <li>Le prestataire vous contacte pour préciser les détails si nécessaire</li>
                      <li>Un devis vous est proposé</li>
                      <li>Après validation et paiement, le service est réalisé</li>
                    </ol>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting || !user}
                    className="w-full py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white text-lg font-semibold rounded-xl hover:from-green-700 hover:to-emerald-700 transition shadow-lg disabled:opacity-50"
                  >
                    {submitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                        Envoi en cours...
                      </span>
                    ) : (
                      '✓ Envoyer ma demande'
                    )}
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>

        {/* Trust badges */}
        <div className="mt-8 grid grid-cols-3 gap-4 text-center">
          <div className="bg-white rounded-xl p-4 shadow">
            <span className="text-2xl">🔒</span>
            <p className="text-sm text-gray-600 mt-2">Paiement sécurisé</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow">
            <span className="text-2xl">⭐</span>
            <p className="text-sm text-gray-600 mt-2">Service de qualité</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow">
            <span className="text-2xl">💬</span>
            <p className="text-sm text-gray-600 mt-2">Support réactif</p>
          </div>
        </div>
      </div>

      {/* Modal de notification */}
      {modal.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 transform animate-[fadeIn_0.2s_ease-out]">
            <div className="text-center">
              {modal.type === 'success' ? (
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              ) : (
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              )}
              <h3 className={`text-xl font-bold mb-2 ${modal.type === 'success' ? 'text-green-700' : 'text-red-700'}`}>
                {modal.type === 'success' ? 'Succès !' : 'Erreur'}
              </h3>
              <p className="text-gray-600 mb-6">{modal.message}</p>
              <button
                onClick={() => {
                  setModal({ show: false, type: '', message: '' });
                  if (modal.type === 'success') {
                    navigate('/my-service-requests');
                  }
                }}
                className={`w-full py-3 rounded-xl font-semibold text-white transition ${
                  modal.type === 'success' 
                    ? 'bg-green-500 hover:bg-green-600' 
                    : 'bg-red-500 hover:bg-red-600'
                }`}
              >
                {modal.type === 'success' ? 'Voir mes demandes' : 'Fermer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceDetails;
